import { CAPABILITIES } from '../security/permissions';
import { hasAllCapabilities } from '../helpers/authSession';

export const routePaths = {
  login: '/login',
  root: '/',
  dashboard: '/dashboard',
  transactions: '/transactions',
  details: '/details',
  detailsById: '/details/:id',
  status: '/status',
  rulesPlan: '/client-rules',
  rulesPlanGroupEdit: '/client-rules/group/:group',
  rulesPlanRiskEdit: '/client-rules/risk',
  adminPlans: '/admin/plans',
  policyGovernance: '/admin/policies',
  adminTenancy: '/admin/organizations-users',
  example: '/example',
};

export const SCREEN_ACCESS_MATRIX = {
  dashboard: {
    routeCapabilities: [CAPABILITIES.DASHBOARD_VIEW],
    menuCapabilities: [CAPABILITIES.DASHBOARD_VIEW],
    backendGuard: 'authenticated current_client_id scoped endpoints',
  },
  transactions: {
    routeCapabilities: [CAPABILITIES.TRANSACTIONS_VIEW],
    menuCapabilities: [CAPABILITIES.TRANSACTIONS_VIEW],
    backendGuard: 'authenticated current_client_id scoped endpoints',
  },
  transactionDetails: {
    routeCapabilities: [CAPABILITIES.TRANSACTIONS_DETAILS_VIEW],
    menuCapabilities: [CAPABILITIES.TRANSACTIONS_VIEW],
    backendGuard: 'authenticated current_client_id scoped endpoints',
  },
  transactionStatus: {
    routeCapabilities: [CAPABILITIES.TRANSACTIONS_STATUS_VIEW],
    menuCapabilities: [CAPABILITIES.TRANSACTIONS_VIEW],
    backendGuard: 'analytics endpoints scoped by current_client_id',
  },
  clientRules: {
    routeCapabilities: [CAPABILITIES.POLICY_RULES_VIEW],
    menuCapabilities: [CAPABILITIES.POLICY_RULES_VIEW],
    backendGuard: 'client policy read access + organization isolation',
  },
  clientRulesEdit: {
    routeCapabilities: [CAPABILITIES.POLICY_RULES_MANAGE],
    menuCapabilities: [CAPABILITIES.POLICY_RULES_VIEW],
    backendGuard: 'require_policy_admin_role + manageable client checks',
  },
  adminPlans: {
    routeCapabilities: [CAPABILITIES.ADMIN_PLANS_VIEW],
    menuCapabilities: [CAPABILITIES.ADMIN_PLANS_VIEW],
    backendGuard: 'require_read_admin_role for read, require_policy_admin_role for write',
  },
  policyGovernance: {
    routeCapabilities: [CAPABILITIES.POLICY_RULES_VIEW],
    menuCapabilities: [CAPABILITIES.POLICY_RULES_VIEW],
    backendGuard: 'client policy read access + require_policy_admin_role for write',
  },
  adminTenancy: {
    routeCapabilities: [CAPABILITIES.TENANCY_VIEW],
    menuCapabilities: [CAPABILITIES.TENANCY_VIEW],
    backendGuard: 'require_read_admin_role for read; organizations write requires require_admin_role, clients write requires require_policy_admin_role',
  },
  example: {
    routeCapabilities: [CAPABILITIES.DASHBOARD_VIEW],
    menuCapabilities: [],
    backendGuard: 'frontend example screen',
  },
};

const routeDefinitions = [
  { paths: [routePaths.root, routePaths.dashboard], section: 'Dashboard', page: 'Home', menuKey: 'dashboard' },
  { paths: [routePaths.transactions], section: 'Transações', page: 'Lista', menuKey: 'transactions' },
  { paths: [routePaths.details], section: 'Transações', page: 'Detalhes', menuKey: 'transactions' },
  { paths: [routePaths.status], section: 'Transações', page: 'Status', menuKey: 'transactions' },
  { paths: [routePaths.rulesPlanGroupEdit], section: 'Cliente', page: 'Editar Regras da Policy', menuKey: 'clientRules' },
  { paths: [routePaths.rulesPlanRiskEdit], section: 'Cliente', page: 'Editar Risco Global da Policy', menuKey: 'clientRules' },
  { paths: [routePaths.rulesPlan], section: 'Cliente', page: 'Regras da Policy', menuKey: 'clientRules' },
  { paths: [routePaths.adminPlans], section: 'Admin', page: 'Planos Base', menuKey: 'adminPlans' },
  { paths: [routePaths.policyGovernance], section: 'Admin', page: 'Policies de Clientes', menuKey: 'policyGovernance' },
  { paths: [routePaths.adminTenancy], section: 'Admin', page: 'Organizações e Usuários', menuKey: 'adminTenancy' },
  { paths: [routePaths.example], section: 'Dashboard', page: 'Exemplo' },
];

export const MENU_ACCESS_MATRIX = Object.fromEntries(
  Object.entries(SCREEN_ACCESS_MATRIX).map(([key, config]) => [key, config.menuCapabilities || []]),
);

export const menuItems = [
  {
    key: 'dashboard',
    text: 'Dashboard',
    path: routePaths.dashboard,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.dashboard.menuCapabilities,
  },
  {
    key: 'transactions',
    text: 'Transações',
    path: routePaths.transactions,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.transactions.menuCapabilities,
  },
  {
    key: 'clientRules',
    text: 'Regras da Policy',
    path: routePaths.rulesPlan,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.clientRules.menuCapabilities,
  },
  {
    key: 'adminPlans',
    text: 'Planos Base',
    path: routePaths.adminPlans,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.adminPlans.menuCapabilities,
  },
  {
    key: 'policyGovernance',
    text: 'Policies de Clientes',
    path: routePaths.policyGovernance,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.policyGovernance.menuCapabilities,
  },
  {
    key: 'adminTenancy',
    text: 'Organizações e Usuários',
    path: routePaths.adminTenancy,
    requiredCapabilities: SCREEN_ACCESS_MATRIX.adminTenancy.menuCapabilities,
  },
];

// Fase 2 (enforced): a decisao vem so de `permissions` da sessao atual (via
// helpers/authSession.hasAllCapabilities) -- nao depende mais de papel.
export const getVisibleMenuItems = () => menuItems.filter((item) => (
  !Array.isArray(item.requiredCapabilities) || !item.requiredCapabilities.length || hasAllCapabilities(item.requiredCapabilities)
));

const isPathActive = (pathname, routePath) => pathname === routePath || pathname.startsWith(`${routePath}/`);

export const getBreadcrumbByPath = (pathname) => {
  const matchedRoute = routeDefinitions.find((route) => route.paths.some((routePath) => isPathActive(pathname, routePath)));

  if (matchedRoute) {
    return { section: matchedRoute.section, page: matchedRoute.page };
  }

  return { section: 'Dashboard', page: 'Home' };
};

export const isMenuItemSelectedByPath = (pathname, menuItem) => {
  if (isPathActive(pathname, menuItem.path)) {
    return true;
  }

  return routeDefinitions.some(
    (route) => route.menuKey === menuItem.key && route.paths.some((routePath) => isPathActive(pathname, routePath)),
  );
};
